import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, Pencil, RefreshCw, Search, Upload, X } from 'lucide-react';
import { adminService, WmiRow } from '../services/AdminService';

// Admin view of wmi_manufacturer (VIN prefix → catalog symbol). Filtering is
// server-side; the CSV upload upserts by WMI and rejects the whole file on any
// bad line (backend: routes/admin.py upload_wmi_csv). Rows edit inline
// (pencil or double-click; Enter saves, Esc cancels) via PUT /wmi/{wmi}.
const PAGE = 100;

const WmiTab: React.FC = () => {
    const [qDraft, setQDraft] = useState('');
    const [q, setQ] = useState('');
    const [source, setSource] = useState('');
    const [known, setKnown] = useState<'' | 'true' | 'false'>('');
    const [offset, setOffset] = useState(0);
    const [rows, setRows] = useState<WmiRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadMsg, setUploadMsg] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState({ symbol: '', manufacturer: '' });
    const [saving, setSaving] = useState(false);

    const filters = useMemo(() => ({
        q: q || undefined,
        source: source || undefined,
        known: known === '' ? undefined : known === 'true',
    }), [q, source, known]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await adminService.listWmi({ ...filters, limit: PAGE, offset });
            setRows(resp.data.rows);
            setTotal(resp.data.total);
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [filters, offset]);

    useEffect(() => { load(); }, [load]);

    const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        setUploadMsg(null);
        try {
            const resp = await adminService.uploadWmiCsv(file);
            setUploadMsg(`Upserted ${resp.data.upserted} rows from ${file.name}`);
            load();
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setUploadMsg(detail?.errors ? `Rejected: ${detail.errors.join('; ')}` : (detail || err?.message || 'Upload failed'));
        } finally {
            setUploading(false);
        }
    };

    // Exports what the current filters show (all pages), in the upload format.
    const onExport = async () => {
        try {
            const resp = await adminService.exportWmiCsv(filters);
            const type = String(resp.headers['content-type'] || '');
            if (!type.includes('text/csv')) {
                setError(`Export returned ${type || 'an unknown type'} instead of CSV`);
                return;
            }
            const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `wmi_${new Date().toISOString().slice(0, 10)}.csv`;
            // Firefox/Safari ignore clicks on a detached anchor and abort if the
            // URL is revoked before the download starts.
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e: any) {
            setError(e?.message || 'Export failed');
        }
    };

    const startEdit = (r: WmiRow) => {
        setEditing(r.wmi);
        setDraft({ symbol: r.symbol, manufacturer: r.manufacturer || '' });
    };

    const saveEdit = async () => {
        if (!editing || saving) return;
        setSaving(true);
        setError(null);
        try {
            const { row } = (await adminService.updateWmi(editing, { symbol: draft.symbol, manufacturer: draft.manufacturer || null })).data;
            setRows(prev => prev.map(r => (r.wmi === row.wmi ? row : r)));
            setEditing(null);
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const onEditKey = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') saveEdit();
        if (e.key === 'Escape') setEditing(null);
    };

    const applySearch = (e: React.FormEvent) => {
        e.preventDefault();
        setOffset(0);
        setQ(qDraft.trim());
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <form onSubmit={applySearch} className="flex items-center gap-2 flex-1 min-w-[240px]">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={qDraft}
                                onChange={e => setQDraft(e.target.value)}
                                placeholder="WMI prefix, symbol or manufacturer"
                                className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-md text-sm"
                            />
                        </div>
                        <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">Search</button>
                    </form>
                    <select value={source} onChange={e => { setOffset(0); setSource(e.target.value); }} className="px-2 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">All sources</option>
                        <option value="bigquery">bigquery</option>
                        <option value="admin">admin</option>
                    </select>
                    <select value={known} onChange={e => { setOffset(0); setKnown(e.target.value as any); }} className="px-2 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Known + unknown</option>
                        <option value="true">Has symbol</option>
                        <option value="false">No symbol</option>
                    </select>
                    <button onClick={load} className="p-2 border border-gray-300 rounded-md" title="Refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button onClick={onExport} className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                    <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} />
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md text-sm disabled:opacity-50"
                    >
                        <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload CSV'}
                    </button>
                </div>
                <p className="text-xs text-gray-500">
                    CSV columns: <code>wmi,symbol,manufacturer</code> (header required, manufacturer optional). Rows are upserted by WMI; an empty symbol marks the prefix as unknown. Any invalid line rejects the whole file.
                </p>
                {uploadMsg && <p className="text-sm text-gray-700">{uploadMsg}</p>}
                {error && <p className="text-sm text-red-600">{String(error)}</p>}
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-600">
                        <tr>
                            <th className="px-4 py-2">WMI</th>
                            <th className="px-4 py-2">Symbol</th>
                            <th className="px-4 py-2">Manufacturer</th>
                            <th className="px-4 py-2">Source</th>
                            <th className="px-4 py-2">Updated</th>
                            <th className="px-4 py-2 w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => editing === r.wmi ? (
                            <tr key={r.wmi} className="border-t border-gray-100 bg-blue-50/40">
                                <td className="px-4 py-2 font-mono">{r.wmi}</td>
                                <td className="px-4 py-1">
                                    <input
                                        autoFocus
                                        value={draft.symbol}
                                        onChange={e => setDraft(d => ({ ...d, symbol: e.target.value }))}
                                        onKeyDown={onEditKey}
                                        placeholder="empty = unknown"
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                </td>
                                <td className="px-4 py-1">
                                    <input
                                        value={draft.manufacturer}
                                        onChange={e => setDraft(d => ({ ...d, manufacturer: e.target.value }))}
                                        onKeyDown={onEditKey}
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                </td>
                                <td className="px-4 py-2">{r.source}</td>
                                <td className="px-4 py-2 text-gray-500">{r.updated_at ? new Date(r.updated_at).toLocaleString() : ''}</td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                    <button onClick={saveEdit} disabled={saving} className="p-1 text-green-700 hover:bg-green-50 rounded disabled:opacity-50" title="Save (Enter)">
                                        <Check className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setEditing(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Cancel (Esc)">
                                        <X className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={r.wmi} className="border-t border-gray-100 group" onDoubleClick={() => startEdit(r)}>
                                <td className="px-4 py-2 font-mono">{r.wmi}</td>
                                <td className="px-4 py-2">{r.symbol || <span className="text-gray-400">—</span>}</td>
                                <td className="px-4 py-2">{r.manufacturer || ''}</td>
                                <td className="px-4 py-2">{r.source}</td>
                                <td className="px-4 py-2 text-gray-500">{r.updated_at ? new Date(r.updated_at).toLocaleString() : ''}</td>
                                <td className="px-4 py-2">
                                    <button onClick={() => startEdit(r)} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded" title="Edit">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No rows</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE, total)}`} of {total}</span>
                <div className="flex gap-2">
                    <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-40">Prev</button>
                    <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-40">Next</button>
                </div>
            </div>
        </div>
    );
};

export default WmiTab;
